import { useEffect, useState } from "react";
import { MessageCircleHeart, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Reader comments for a book.
 * - Anyone can read.
 * - Only Google-signed-in readers can post.
 * - Readers can delete their own; admins can delete any.
 */
export default function BookComments({ bookId }) {
  const { user, signInWithGoogle } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const isReader = !!(user && user.role === "reader");
  const isAdmin = !!(user && user.role === "admin");
  const currentUid = user ? user.user_id || user.id : null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/books/${bookId}/comments`)
      .then(({ data }) => { if (!cancelled) setComments(data || []); })
      .catch(() => { if (!cancelled) setComments([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [bookId]);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/books/${bookId}/comments`, { text: trimmed });
      setComments((prev) => [data, ...prev]);
      setText("");
      toast.success("Thanks for sharing! 💌");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't post that comment.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      setComments((prev) => prev.filter((c) => c.id !== id));
      toast.success("Comment removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't remove that.");
    }
  };

  return (
    <section className="mt-12" data-testid="book-comments">
      <div className="flex items-center gap-2 mb-4">
        <span
          className="w-9 h-9 rounded-2xl flex items-center justify-center"
          style={{ background: "#FFE1EC", color: "#D6407E" }}
        >
          <MessageCircleHeart size={18} />
        </span>
        <h3 className="font-fredoka text-2xl" style={{ color: "#2D3055" }}>Readers' notes</h3>
      </div>

      {isReader ? (
        <form onSubmit={submit} className="paper-card p-4 mb-6" data-testid="book-comment-form">
          <div className="flex gap-3 items-start">
            {user.picture ? (
              <img
                src={user.picture}
                alt=""
                referrerPolicy="no-referrer"
                className="w-10 h-10 rounded-full object-cover shrink-0"
              />
            ) : (
              <span
                className="w-10 h-10 rounded-full flex items-center justify-center font-fredoka text-sm shrink-0"
                style={{ background: "#FF6B9D", color: "#fff" }}
              >
                {(user.name || user.email || "R").charAt(0).toUpperCase()}
              </span>
            )}
            <div className="flex-1">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={`Share what your little one loved, ${(user.name || "friend").split(" ")[0]}…`}
                rows={3}
                maxLength={2000}
                required
                className="w-full px-4 py-3 rounded-2xl border outline-none font-nunito text-sm resize-none"
                style={{ borderColor: "#F0EBFF", background: "#FFFFFF", color: "#2D3055" }}
                data-testid="book-comment-input"
              />
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs font-nunito" style={{ color: "#7E82A1" }}>
                  Kind words only — this is a wholesome place ✨
                </span>
                <button
                  type="submit"
                  disabled={busy || !text.trim()}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full font-fredoka text-sm text-white disabled:opacity-60"
                  style={{
                    background: "#FF6B9D",
                    boxShadow: "0 8px 18px rgba(255,107,157,0.30)",
                  }}
                  data-testid="book-comment-submit"
                >
                  {busy ? "Posting…" : (<><Send size={14} /> Share</>)}
                </button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div
          className="rounded-3xl p-5 mb-6 flex items-center justify-between gap-4 flex-wrap"
          style={{
            background: "linear-gradient(135deg,#FFF3F8 0%,#F0EBFF 100%)",
            border: "1px solid #F0EBFF",
          }}
          data-testid="book-comment-signin-prompt"
        >
          <p className="font-nunito text-sm" style={{ color: "#2D3055" }}>
            <span className="font-fredoka" style={{ color: "#D6407E" }}>Sign in with Google</span>
            {" "}to leave a note for M.K. and other little-hearted readers.
          </p>
          <button
            type="button"
            onClick={signInWithGoogle}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-fredoka text-sm"
            style={{
              background: "#FFFFFF",
              color: "#2D3055",
              border: "1px solid #C7B5FF",
              boxShadow: "0 6px 14px rgba(45,48,85,0.06)",
            }}
            data-testid="book-comment-signin-btn"
          >
            Sign in with Google
          </button>
        </div>
      )}

      {loading ? (
        <p className="font-nunito text-sm" style={{ color: "#7E82A1" }}>Turning the pages…</p>
      ) : comments.length === 0 ? (
        <p
          className="font-caveat text-2xl"
          style={{ color: "#7E82A1" }}
          data-testid="book-comments-empty"
        >
          Be the first to share what this book meant to your family.
        </p>
      ) : (
        <ul className="space-y-4" data-testid="book-comments-list">
          {comments.map((c) => {
            const canDelete = isAdmin || (currentUid && c.user_id === currentUid);
            const when = new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
            return (
              <li
                key={c.id}
                className="paper-card p-4"
                data-testid={`book-comment-${c.id}`}
              >
                <div className="flex gap-3">
                  {c.user_picture ? (
                    <img
                      src={c.user_picture}
                      alt=""
                      referrerPolicy="no-referrer"
                      className="w-10 h-10 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <span
                      className="w-10 h-10 rounded-full flex items-center justify-center font-fredoka text-sm shrink-0"
                      style={{ background: "#C7B5FF", color: "#2D3055" }}
                    >
                      {(c.user_name || "R").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-fredoka" style={{ color: "#2D3055" }}>
                        {c.user_name || "Reader"}
                      </span>
                      <span className="text-xs font-nunito" style={{ color: "#7E82A1" }}>{when}</span>
                    </div>
                    <p className="mt-1 font-nunito text-sm whitespace-pre-wrap" style={{ color: "#3D405B" }}>
                      {c.text}
                    </p>
                  </div>
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => remove(c.id)}
                      className="p-2 rounded-full transition-colors hover:bg-[#FFE1EC]"
                      aria-label="Delete comment"
                      data-testid={`book-comment-delete-${c.id}`}
                    >
                      <Trash2 size={14} style={{ color: "#D6407E" }} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
